<script lang="ts" setup>

	import ProductTableHeader from '@/components/table/producttable/ProductTableHeader.vue';
	import {onMounted, ref, watchEffect} from 'vue';
	import {vScroll} from '@vueuse/components';
	import type {StaticTableHeader, TableSort} from '@/types/table';
	import {useElementHover, type UseScrollReturn} from '@vueuse/core';
	import {
		recalculateTableColumnWidths,
		useFixedHeader
	} from '@/components/table/fixedtableheader/fixed.table.header.helper';
	import {
		EXTRA_SCROLL_WIDTH,
		scrollToNextColumn,
		scrollToPrevColumn,
		syncDynamicTableHeaderScrollPosition,
		syncFixedTableHeaderScrollPosition
	} from 'dummen-shared';

	defineProps<{
		productHeaders: Array<StaticTableHeader>;
		tableSort: TableSort | null;
		tableClass: string;
	}>();

	const emit = defineEmits(['on-update-sort', 'on-toggle-filters']);

	const scrollElement = ref();
	const isScrollStart = ref(true);
	const isScrollEnd = ref(false);

	const showFixedHeader = useFixedHeader();
	const scrollElementIsUsed = useElementHover(scrollElement);

	watchEffect(() => {
		if (showFixedHeader.value) {
			syncFixedTableHeaderScrollPosition();
		}
	});

	const updateSort = (sort: TableSort) => emit('on-update-sort', sort);

	const toggleFilters = () => emit('on-toggle-filters');

	const onScroll = (state: UseScrollReturn) => {
		isScrollStart.value = state.arrivedState.left || state.x.value <= EXTRA_SCROLL_WIDTH;
		isScrollEnd.value = state.arrivedState.right;

		// Only sync scrolling when this specific scroll element is used to break the circle.
		// Without this it's not possible to use smooth scrolling.
		if (scrollElementIsUsed.value) {
			syncDynamicTableHeaderScrollPosition();
		}
	};

	onMounted(() => recalculateTableColumnWidths());

</script>

<template>
	<Teleport to=".availability-wrapper">
		<div :class="{'show-fixed-header' : showFixedHeader}" class="header-catalog-fixed">
			<div class="header-inner">

				<div class="header-catalog-fixed__filter">
					<button type="button" @click="toggleFilters">
				        <span class="icon">
				            <svg class="icon-arrow-right" height="17" width="19">
				                <use xlink:href="/assets/images/build/svg-sprite.catalog.min.svg#arrow-right"></use>
				            </svg>
				        </span>
					</button>

					<span class="text" @click="toggleFilters">
						{{ $t('site.products.overview.filters.title') }}
					</span>
				</div>

				<div class="catalog-overview-table-wrapper header-catalog-fixed__thead">
					<div class="catalog-overview-table-wrapper__left js-fixed-static-table-wrapper">
						<table :class="tableClass" class="catalog-overview-table">
							<thead>
								<tr>
									<th></th>
									<th></th>
									<ProductTableHeader v-for="header in productHeaders"
									                    :key="header.key"
									                    :colspan="header.colspan"
									                    :field-key="header.key"
									                    :field-translation="header.content"
									                    :sort="tableSort"
									                    :sortable="header.sortable"
									                    @sort-updated="updateSort($event)" />
								</tr>
							</thead>
						</table>

						<button v-show="!isScrollStart"
						        class="tablenavigation tablenavigation--prev"
						        type="button"
						        @click="scrollToPrevColumn">

							<svg class="icon-triangle-left" height="11" width="8">
								<use xlink:href="/assets/images/build/svg-sprite.catalog.min.svg#triangle-left"></use>
							</svg>
						</button>
					</div>

					<div ref="scrollElement"
					     v-scroll="onScroll"
					     class="catalog-overview-table-wrapper__right js-fixed-dynamic-table-wrapper">
						<table :class="tableClass" class="catalog-overview-table">
							<thead>
								<tr>
									<th data-column="tablenavigation-spacing" scope="col"></th>

									<slot></slot>

									<th data-column="tablenavigation-spacing" scope="col"></th>
								</tr>
							</thead>
						</table>
					</div>

					<button v-show="!isScrollEnd"
					        class="tablenavigation tablenavigation--next"
					        type="button"
					        @click="scrollToNextColumn">

						<svg class="icon-triangle-right" height="11" width="8">
							<use xlink:href="/assets/images/build/svg-sprite.catalog.min.svg#triangle-right"></use>
						</svg>
					</button>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<style lang="scss" scoped>
	.header-catalog-fixed {
		position: fixed;
		top: calc(114px + (var(--header-extra-top-for-shoppingcart) * 1px));
		z-index: 10;

		.header-inner {
			width: 100%;
		}

		&.show-fixed-header {
			visibility: visible;
		}
	}

	.catalog-overview-table-wrapper__left {
		table > thead > tr {
			th:nth-child(1) {
				width: 26px;
			}

			th:nth-child(2) {
				width: 68px;
			}
		}
	}
</style>
